import classNames from 'classnames';
import React, { ElementRef, useRef, CSSProperties } from 'react';

interface InputProps {
  value: string | number | undefined;
  onChange?: (value: string | number) => void;
  disabled?: boolean;
  type?: 'text' | 'tel' | 'number' | 'email' | 'password';
  prefix?: string;
  suffix?: string;
  className?: string;
  pattern?: string;
  placeholder?: string;
  min?: number;
  max?: number;
  size?: 'xs' | 'sm';
  name?: string;
  id?: string;
  required?: boolean;
  autoFocus?: boolean;
  style?: CSSProperties;
  onBlur?: () => void;
  onFocus?: () => void;
  onClick?: (e?: any) => void;
  inputMode?: 'numeric';
  customKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const Input = ({
  value,
  onChange,
  disabled,
  type = 'text',
  min,
  max,
  size,
  className,
  prefix,
  suffix,
  customKeyDown,
  style,
  ...rest
}: InputProps) => {
  const inputRef = useRef<ElementRef<'input'>>();

  const _onChange = (e) => {
    if (disabled || !onChange) return;

    if (min !== undefined && e.target.value !== '' && e.target.value < min) return;
    if (max !== undefined && e.target.value > max) return;

    if (type === 'number') {
      const val =
        e.target.value !== undefined && e.target.value !== '' ? +e.target.value : e.target.value;
      return onChange(val);
    }

    onChange(e.target.value);
  };

  const getValue = () => {
    if (type === 'number') {
      if (value === undefined || value === null || value === '') return '';
      return +value;
    }
    return value || '';
  };

  const classname = classNames('form-control', {
    'is-xs': size === 'xs',
    'is-sm': size === 'sm',
    'form-control--with-prefix': prefix,
    'form-control--with-suffix': suffix,
    [className]: !!className,
  });

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      inputRef.current?.blur();
    }
  };

  const handleWheel = (e) => {
    if (inputRef.current && document.activeElement === inputRef.current) {
      inputRef.current.blur();
      e.stopPropagation();
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const inputStyle = {
    ...(style || {}),
    ...(type === 'password' ? { backgroundColor: 'white' } : {}),
  };

  return (
    <>
      {prefix ? <div className="input-prefix">{prefix}</div> : null}
      <input
        ref={inputRef}
        className={classname}
        value={getValue()}
        onChange={_onChange}
        disabled={disabled}
        type={type}
        min={min}
        max={max}
        onKeyDown={customKeyDown || handleKeyDown}
        onWheel={handleWheel}
        style={inputStyle}
        {...rest}
      />
      {suffix ? <div className="input-suffix">{suffix}</div> : null}
    </>
  );
};

export default Input;
